import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Loader } from '@/components/loading/Loading';
import { useModal } from '@/components/modal/useModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/tabs/Tabs';
import SiSearch from '@/icons/SiSearch';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { PageLoading } from '@/layouts/PageLoading';
import { Container } from '@/layouts/main/Main';
import { MappedCustomerCreditCheck, useCustomerCreditChecks } from '@/services/api/hooks/credit';
import { PageTitle } from '@/utils/Title';
import { useSearch } from '@/utils/search';
import { FunctionComponent, Suspense, useState } from 'react';
import CreditCheckTable, { CreditCheckTableData } from '../CreditCheckTable';
import { CreditCheckStatus } from '../creditCheckHelper';
import CreditCheckStatusFilter from './CreditCheckStatusFilter';
import EditCreditCheckModal from './EditCreditCheckModal';

const columns = [
  {
    accessor: 'name',
    sortable: true,
    text: 'Customer Name'
  },
  {
    accessor: 'number',
    sortable: true,
    text: 'Company Number',
    size: '0.5fr'
  },
  {
    accessor: 'creditLimit',
    sortable: true,
    text: 'Credit Limit',
    size: '0.6fr'
  },
  {
    accessor: 'createdAt',
    sortable: true,
    text: 'Created At',
    size: '0.8fr'
  },
  {
    accessor: 'lastModified',
    sortable: true,
    text: 'Last Modified',
    size: '0.8fr'
  },
  {
    accessor: 'userId',
    sortable: true,
    text: 'Modified By'
  },
  {
    accessor: 'status',
    sortable: true,
    text: 'Status',
    size: '0.8fr'
  },
  {
    accessor: '',
    sortable: false,
    text: '',
    size: '0.2fr'
  }
];

interface TabContentProps {
  data: MappedCustomerCreditCheck[];
}

const TabContent: FunctionComponent<TabContentProps> = ({ data }) => {
  const modalProps = useModal<{ mspId: string; customerId: string; creditCheckId?: string; regionCode: string }>();

  return (
    <Suspense
      fallback={
        <div className="w-full py-8 flex justify-center items-center">
          <Loader className="h-8 w-8" />
        </div>
      }
    >
      {data.length > 0 ? (
        <>
          <EditCreditCheckModal {...modalProps} />
          <CreditCheckTable
            onEdit={(regionCode: string, mspId: string, creditCheckId?: string, customerId?: string) =>
              modalProps.open({ mspId, customerId: customerId!, creditCheckId, regionCode })
            }
            headers={columns}
            data={data as CreditCheckTableData[]}
          />
        </>
      ) : (
        <p className="font-semibold text-lg mt-10">No credit checks available</p>
      )}
    </Suspense>
  );
};

const CreditCheckContent: FunctionComponent = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Credit Check',
      to: ''
    }
  ];

  const [data] = useCustomerCreditChecks();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const [filterStatusQuery, setFilterStatusQuery] = useState<string>('');

  const filteredCreditChecksByName = useSearch(data, ['name'], searchQuery);

  const filteredCreditChecksByStatusAndName = useSearch(filteredCreditChecksByName, ['status'], filterStatusQuery, {
    useExtendedSearch: true
  });

  const reviewItems = filteredCreditChecksByStatusAndName.filter(
    item =>
      item.creditLimit === undefined ||
      item.status === CreditCheckStatus.manuallyReferred ||
      item.status === CreditCheckStatus.pending
  );

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Credit Check</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto overflow-y-auto">
        <Container animate="pop" className="w-full py-8">
          <Tabs defaultValue={`${reviewItems.length ? 'reviewNeeded' : 'all'}`}>
            <div className="space-y-10">
              <div>
                <TabsList>
                  <TabsTrigger
                    className="px-4 text-2xl border-black data-[state=active]:border-b-[3px] data-[state=active]:text-black data-[state=active]:font-semibold"
                    value="all"
                  >
                    All Customers ({data.length})
                  </TabsTrigger>
                  <TabsTrigger
                    className="px-4 text-2xl border-black data-[state=active]:border-b-[3px] data-[state=active]:text-black data-[state=active]:font-semibold"
                    value="reviewNeeded"
                  >
                    Review Needed ({reviewItems.length})
                  </TabsTrigger>
                </TabsList>
              </div>
              <div className="space-y-3">
                <div className="flex space-x-5">
                  <TextInput
                    id="filter"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={'Search for customers'}
                    prefix={<SiSearch className="text-xl" />}
                  />
                  <CreditCheckStatusFilter onChangeCallback={setFilterStatusQuery} />
                </div>
                <TabsContent value="all">
                  <TabContent data={filteredCreditChecksByStatusAndName} />
                </TabsContent>
                <TabsContent value="reviewNeeded">
                  <TabContent data={reviewItems} />
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </Container>
      </div>
    </>
  );
};

const CreditCheckPage: FunctionComponent = () => {
  return (
    <>
      <PageTitle>Credit Check</PageTitle>
      <Suspense fallback={<PageLoading />}>
        <CreditCheckContent />
      </Suspense>
    </>
  );
};

export default CreditCheckPage;
